import React, { useState, useRef } from 'react';
import { useTheme } from '../context/ThemeContext';
import { useServices } from '../context/ServiceContext';
import Sidebar from '../components/Sidebar';
import Topbar from '../components/Topbar';
import api from '../api/client';
import { onboardingMemberNames } from '../config/team';
import './Projects.css';

const DEFAULT_COLUMNS = [
  { id:'todo',       label:'To Do',       color:'#6B7280' },
  { id:'inprogress', label:'In Progress',  color:'#3B82F6' },
  { id:'review',     label:'In Review',    color:'#F5A623' },
  { id:'done',       label:'Done',         color:'#22C55E' },
];
const STAGE_PALETTE = ['#8B5CF6','#EC4899','#14B8A6','#F97316','#0EA5E9','#84CC16','#EF4444','#3B82F6'];

const DEFAULT_TEAM_NAMES = [];
const PRIORITIES = ['Low','Medium','High','Urgent'];
const PRIORITY_COLOR = { Low:'#6B7280', Medium:'#3B82F6', High:'#F5A623', Urgent:'#EF4444' };

const INIT_TASKS = [];

const today = new Date().toISOString().split('T')[0];
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-IN',{day:'numeric',month:'short'}) : '—';
const isOverdue = (d,s) => d && d < today && s !== 'done';

const PlusIcon  = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>;
const CloseIcon = () => <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>;
const KanbanIcon= () => <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><rect x="3" y="3" width="5" height="18" rx="1"/><rect x="10" y="3" width="5" height="12" rx="1"/><rect x="17" y="3" width="5" height="15" rx="1"/></svg>;
const ListIcon  = () => <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><circle cx="3" cy="6" r="1.5" fill="currentColor"/><circle cx="3" cy="12" r="1.5" fill="currentColor"/><circle cx="3" cy="18" r="1.5" fill="currentColor"/></svg>;

function TaskCard({ task, onDragStart, onClick }) {
  const over = isOverdue(task.due, task.status);
  return (
    <div className={`pj-card${over?' overdue':''}`} draggable onDragStart={()=>onDragStart(task)} onClick={()=>onClick(task)}>
      <div className="pj-card-priority" style={{background: PRIORITY_COLOR[task.priority]+'22', color: PRIORITY_COLOR[task.priority]}}>{task.priority}</div>
      <div className="pj-card-title">{task.title}</div>
      {task.service && <span className="pj-card-tag" style={{background:'#F5A62322',color:'#F5A623'}}>{task.service}</span>}
      {(task.tags||[]).map(t=><span key={t} className="pj-card-tag">{t}</span>)}
      <div className="pj-card-foot">
        <div className="pj-card-av">{(task.assigned||'?')[0]}</div>
        <span className={`pj-card-due${over?' over':''}`}>{over?'⚠ ':'📅 '}{fmtDate(task.due)}</span>
      </div>
    </div>
  );
}

function Column({ col, tasks, onDragStart, onDrop, onCardClick, onRename, onDelete }) {
  const [over, setOver] = useState(false);
  return (
    <div className={`pj-col${over?' drag-over':''}`}
      onDragOver={e=>{e.preventDefault();setOver(true)}}
      onDragLeave={()=>setOver(false)}
      onDrop={()=>{setOver(false);onDrop(col.id)}}>
      <div className="pj-col-hd">
        <div className="pj-col-dot" style={{background:col.color}}/>
        <span className="pj-col-label">{col.label}</span>
        <span className="pj-col-count">{tasks.length}</span>
        {onRename && <span onClick={()=>onRename(col.id)} title="Rename stage" style={{cursor:'pointer',opacity:.45,fontSize:12,paddingLeft:6}}>✎</span>}
        {onDelete && <span onClick={()=>onDelete(col.id)} title="Delete stage" style={{cursor:'pointer',opacity:.45,fontSize:15,lineHeight:1,paddingLeft:4}}>×</span>}
      </div>
      <div className="pj-col-body">
        {tasks.map(t=><TaskCard key={t.id} task={t} onDragStart={onDragStart} onClick={onCardClick}/>)}
        {tasks.length===0 && <div className="pj-empty">Drop here</div>}
      </div>
    </div>
  );
}

function TaskPanel({ task, theme, onClose, onUpdate, onDelete, teamNames, columns }) {
  const { services } = useServices();
  const [t, setT] = useState(task);
  const set = (k,v) => setT(p=>({...p,[k]:v}));
  return (
    <div className="pj-panel-wrap">
      <div className="pj-overlay" onClick={onClose}/>
      <div className="pj-panel" data-theme={theme}>
        <div className="pj-panel-hd">
          <span className="pj-panel-title">Edit Task</span>
          <button className="pj-icon-btn" onClick={onClose}><CloseIcon/></button>
        </div>
        <div className="pj-panel-body">
          <div className="pj-panel-sec">
            <label className="pj-lbl">Title</label>
            <input className="pj-inp" value={t.title} onChange={e=>set('title',e.target.value)}/>
          </div>
          <div className="pj-panel-sec">
            <label className="pj-lbl">Description</label>
            <textarea className="pj-inp pj-ta" rows={3} value={t.desc} onChange={e=>set('desc',e.target.value)}/>
          </div>
          <div className="pj-panel-sec">
            <label className="pj-lbl">Service</label>
            <select className="pj-inp pj-sel" value={t.service||''} onChange={e=>set('service',e.target.value)}>
              <option value="">No service set</option>
              {services.map(s=><option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div className="pj-form-row">
            <div className="pj-panel-sec">
              <label className="pj-lbl">Status</label>
              <select className="pj-inp pj-sel" value={t.status} onChange={e=>set('status',e.target.value)}>
                {(columns && columns.length ? columns : DEFAULT_COLUMNS).map(c=><option key={c.id} value={c.id}>{c.label}</option>)}
              </select>
            </div>
            <div className="pj-panel-sec">
              <label className="pj-lbl">Priority</label>
              <select className="pj-inp pj-sel" value={t.priority} onChange={e=>set('priority',e.target.value)}>
                {PRIORITIES.map(p=><option key={p}>{p}</option>)}
              </select>
            </div>
          </div>
          <div className="pj-form-row">
            <div className="pj-panel-sec">
              <label className="pj-lbl">Assigned to</label>
              <select className="pj-inp pj-sel" value={t.assigned} onChange={e=>set('assigned',e.target.value)}>
                {[...new Set([t.assigned, ...(teamNames||DEFAULT_TEAM_NAMES)].filter(Boolean))].map(m=><option key={m}>{m}</option>)}
              </select>
            </div>
            <div className="pj-panel-sec">
              <label className="pj-lbl">Due date</label>
              <input className="pj-inp" type="date" value={t.due} onChange={e=>set('due',e.target.value)}/>
            </div>
          </div>
        </div>
        <div className="pj-panel-ft" style={{display:'flex',gap:'10px'}}>
          <button className="pj-btn-sec" style={{color:'#EF4444',borderColor:'rgba(239,68,68,0.35)'}}
            onClick={()=>{ if(window.confirm('Delete this task permanently? This cannot be undone.')){ onDelete(t.id); onClose(); } }}>Delete</button>
          <button className="pj-btn-pri" style={{flex:1}} onClick={()=>{onUpdate(t);onClose();}}>Save changes</button>
        </div>
      </div>
    </div>
  );
}

function AddModal({ theme, onClose, onAdd, teamNames, columns }) {
  const { services } = useServices();
  const cols = (columns && columns.length) ? columns : DEFAULT_COLUMNS;
  const [form, setForm] = useState({ title:'', desc:'', service:'', status:cols[0].id, priority:'Medium', assigned:'', due:'', tags:[] });
  const set = (k,v) => setForm(f=>({...f,[k]:v}));
  return (
    <div className="pj-modal-overlay" onClick={onClose}>
      <div className="pj-modal" data-theme={theme} onClick={e=>e.stopPropagation()}>
        <div className="pj-modal-hd">
          <span className="pj-modal-title">Add Task</span>
          <button className="pj-icon-btn" onClick={onClose}><CloseIcon/></button>
        </div>
        <div className="pj-modal-body">
          <div className="pj-panel-sec"><label className="pj-lbl">Title *</label><input className="pj-inp" placeholder="Task title" value={form.title} onChange={e=>set('title',e.target.value)}/></div>
          <div className="pj-panel-sec"><label className="pj-lbl">Description</label><textarea className="pj-inp pj-ta" rows={2} placeholder="Details…" value={form.desc} onChange={e=>set('desc',e.target.value)}/></div>
          <div className="pj-panel-sec"><label className="pj-lbl">Service</label><select className="pj-inp pj-sel" value={form.service} onChange={e=>set('service',e.target.value)}><option value="">No service</option>{services.map(s=><option key={s} value={s}>{s}</option>)}</select></div>
          <div className="pj-form-row">
            <div className="pj-panel-sec"><label className="pj-lbl">Status</label><select className="pj-inp pj-sel" value={form.status} onChange={e=>set('status',e.target.value)}>{(columns && columns.length ? columns : DEFAULT_COLUMNS).map(c=><option key={c.id} value={c.id}>{c.label}</option>)}</select></div>
            <div className="pj-panel-sec"><label className="pj-lbl">Priority</label><select className="pj-inp pj-sel" value={form.priority} onChange={e=>set('priority',e.target.value)}>{PRIORITIES.map(p=><option key={p}>{p}</option>)}</select></div>
          </div>
          <div className="pj-form-row">
            <div className="pj-panel-sec"><label className="pj-lbl">Assign to</label><select className="pj-inp pj-sel" value={form.assigned} onChange={e=>set('assigned',e.target.value)}>{[...new Set([form.assigned, ...(teamNames||DEFAULT_TEAM_NAMES)].filter(Boolean))].map(m=><option key={m}>{m}</option>)}</select></div>
            <div className="pj-panel-sec"><label className="pj-lbl">Due date</label><input className="pj-inp" type="date" value={form.due} onChange={e=>set('due',e.target.value)}/></div>
          </div>
        </div>
        <div className="pj-modal-ft">
          <button className="pj-btn-sec" onClick={onClose}>Cancel</button>
          <button className="pj-btn-pri" onClick={()=>{if(form.title.trim()){onAdd({...form,id:Date.now(),tags:[]});onClose();}}}>Add Task</button>
        </div>
      </div>
    </div>
  );
}

export default function Projects() {
  const { theme } = useTheme();
  const [tasks, setTasks] = useState([]);
  const [teamNames, setTeamNames] = useState(onboardingMemberNames());
  const [businesses, setBusinesses] = useState([{ id:'main', name:'Main', stages: DEFAULT_COLUMNS }]);
  const [activeBiz, setActiveBiz] = useState('main');

  React.useEffect(() => {
    api.projects.list()
      .then(rows => setTasks(rows.map(p => ({ ...p, assigned: p.assigned_name, due: p.due_date, desc: p.description }))))
      .catch(err => console.error('Failed to load projects:', err));
    api.team.list()
      .then(team => {
        const names = team.map(m => (m.name || '').trim()).filter(Boolean);
        setTeamNames([...new Set([...names, ...onboardingMemberNames()])]);
      })
      .catch(() => setTeamNames(onboardingMemberNames()));
    // Businesses + their own pipelines (isolated per org). Migrate a single board if needed.
    api.settings.get()
      .then(d => {
        if (Array.isArray(d.businesses) && d.businesses.length) {
          setBusinesses(d.businesses); setActiveBiz(d.businesses[0].id);
        } else {
          const stages = (Array.isArray(d.projectStages) && d.projectStages.length) ? d.projectStages : DEFAULT_COLUMNS;
          setBusinesses([{ id:'main', name:'Main', stages }]); setActiveBiz('main');
        }
      })
      .catch(() => {});
  }, []);

  const activeBusiness = businesses.find(b => b.id === activeBiz) || businesses[0] || { id:'main', name:'Main', stages: DEFAULT_COLUMNS };
  const columns = (activeBusiness.stages && activeBusiness.stages.length) ? activeBusiness.stages : DEFAULT_COLUMNS;
  const firstBizId = (businesses[0] || {}).id || 'main';
  const taskBiz = (t) => t.business_id || firstBizId;          // untagged tasks -> first business
  const bizTasks = tasks.filter(t => taskBiz(t) === activeBiz);

  const persistBusinesses = (next) => {
    setBusinesses(next);
    api.settings.save({ businesses: next }).catch(err => console.error('Failed to save businesses:', err));
  };
  const setActiveStages = (stages) => persistBusinesses(businesses.map(b => b.id === activeBiz ? { ...b, stages } : b));

  // In-app dialogs (replaces the browser prompt/confirm popups)
  const [dialog, setDialog] = useState(null);
  const askPrompt  = (title, value, onConfirm) => setDialog({ mode:'prompt', title, value: value || '', onConfirm });
  const askConfirm = (title, message, onConfirm, opts = {}) => setDialog({ mode:'confirm', title, message, danger:opts.danger, confirmLabel:opts.confirmLabel, okOnly:opts.okOnly, onConfirm });

  const addBusiness = () => askPrompt('New business / brand name', '', (name) => {
    if (!name.trim()) return;
    const id = 'biz_' + Date.now();
    persistBusinesses([...businesses, { id, name: name.trim(), stages: DEFAULT_COLUMNS.map(s => ({ ...s })) }]);
    setActiveBiz(id);
  });
  const renameBusiness = () => askPrompt('Rename business', activeBusiness.name, (name) => {
    if (!name.trim()) return;
    persistBusinesses(businesses.map(b => b.id === activeBiz ? { ...b, name: name.trim() } : b));
  });
  const deleteBusiness = () => {
    if (businesses.length <= 1) return askConfirm('Keep one business', 'You need at least one business.', () => {}, { okOnly:true });
    askConfirm('Delete business', `Delete "${activeBusiness.name}"?${bizTasks.length ? ` Its ${bizTasks.length} project(s) will be hidden (not deleted).` : ''}`, () => {
      const next = businesses.filter(b => b.id !== activeBiz);
      persistBusinesses(next); setActiveBiz(next[0].id);
    }, { danger:true, confirmLabel:'Delete' });
  };

  const addStage = () => askPrompt('New stage name', '', (name) => {
    if (!name.trim()) return;
    const color = STAGE_PALETTE[columns.length % STAGE_PALETTE.length];
    setActiveStages([...columns, { id: 'st_' + Date.now(), label: name.trim(), color }]);
  });
  const renameStage = (id) => {
    const cur = columns.find(c => c.id === id);
    askPrompt('Rename stage', cur ? cur.label : '', (name) => {
      if (!name.trim()) return;
      setActiveStages(columns.map(c => c.id === id ? { ...c, label: name.trim() } : c));
    });
  };
  const deleteStage = (id) => {
    if (columns.length <= 1) return askConfirm('Keep one stage', 'You need at least one stage.', () => {}, { okOnly:true });
    const inCol = bizTasks.filter(t => t.status === id);
    const target = columns.find(c => c.id !== id);
    askConfirm('Delete stage', inCol.length ? `Delete this stage? ${inCol.length} task(s) will move to "${target.label}".` : 'Delete this stage?', () => {
      if (inCol.length) {
        setTasks(ts => ts.map(t => (t.status === id && taskBiz(t) === activeBiz) ? { ...t, status: target.id } : t));
        inCol.forEach(t => api.projects.update(t.id, { status: target.id }).catch(() => {}));
      }
      setActiveStages(columns.filter(c => c.id !== id));
    }, { danger:true, confirmLabel:'Delete' });
  };

  const [view, setView]       = useState('kanban');
  const [selected, setSelected] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const dragging = useRef(null);

  const doneId = (columns[columns.length-1] || {}).id || 'done';
  const done = bizTasks.filter(t=>t.status===doneId).length;
  const total = bizTasks.length;
  const overdue = bizTasks.filter(t=>isOverdue(t.due,t.status)).length;

  const handleDrop = (colId) => {
    if (!dragging.current) return;
    const taskId = dragging.current.id;
    setTasks(ts => ts.map(t => t.id===taskId ? {...t,status:colId} : t));
    // Backend handles 30-day follow-up creation + notification when status becomes 'done'
    api.projects.update(taskId, { status: colId }).catch(err => console.error('Failed to update project status:', err));
    dragging.current = null;
  };
  const handleUpdate = (updated) => {
    setTasks(ts => ts.map(t => t.id===updated.id ? updated : t));
    api.projects.update(updated.id, updated).catch(err => console.error('Failed to update project:', err));
  };
  const handleAdd = (task) => {
    const t = { ...task, businessId: activeBiz, business_id: activeBiz };  // tag to the current business
    api.projects.add(t)
      .then(created => setTasks(ts => [created, ...ts.filter(x=>x.id!==t.id)]))
      .catch(err => console.error('Failed to create project:', err));
    setTasks(ts => [t,...ts]);
  };
  const handleDelete = (id) => {
    setTasks(ts => ts.filter(t => t.id !== id));
    api.projects.remove(id).catch(err => console.error('Failed to delete project:', err));
  };

  const TopActions = (
    <div className="pj-top-acts">
      <div className="pj-view-toggle">
        <button className={`pj-vt-btn${view==='kanban'?' active':''}`} onClick={()=>setView('kanban')}><KanbanIcon/></button>
        <button className={`pj-vt-btn${view==='list'?' active':''}`}   onClick={()=>setView('list')}><ListIcon/></button>
      </div>
      <button className="pj-add-btn" onClick={()=>setShowAdd(true)}><PlusIcon/> Add Task</button>
    </div>
  );

  return (
    <div className="pj-wrap" data-theme={theme}>
      <Sidebar/>
      <div className="pj-main">
        <Topbar title="Projects" subtitle={`${done}/${total} done${overdue?` · ⚠ ${overdue} overdue`:''}`} actions={TopActions}/>

        {/* Business selector — each business has its own pipeline & stages */}
        <div className="pj-biz-bar" style={{display:'flex',alignItems:'center',gap:8,flexWrap:'wrap',padding:'14px 20px 2px'}}>
          <span style={{fontSize:11,fontWeight:700,opacity:.55,textTransform:'uppercase',letterSpacing:.5}}>Business</span>
          <select value={activeBiz} onChange={e=>setActiveBiz(e.target.value)}
            style={{padding:'8px 12px',borderRadius:8,border:'1px solid rgba(128,128,128,.35)',background:'transparent',color:'inherit',fontWeight:600,fontSize:13.5,fontFamily:'inherit',cursor:'pointer'}}>
            {businesses.map(b=><option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
          <button type="button" onClick={addBusiness} style={{padding:'8px 12px',borderRadius:8,border:'1px solid #ff6600',background:'rgba(255,102,0,.1)',color:'#ff6600',fontWeight:600,fontSize:13,fontFamily:'inherit',cursor:'pointer'}}>+ Add business</button>
          <button type="button" onClick={renameBusiness} style={{padding:'8px 12px',borderRadius:8,border:'1px solid rgba(128,128,128,.35)',background:'transparent',color:'inherit',fontWeight:600,fontSize:13,fontFamily:'inherit',cursor:'pointer'}}>Rename</button>
          {businesses.length>1 && <button type="button" onClick={deleteBusiness} style={{padding:'8px 12px',borderRadius:8,border:'1px solid rgba(239,68,68,.4)',background:'transparent',color:'#ef4444',fontWeight:600,fontSize:13,fontFamily:'inherit',cursor:'pointer'}}>Delete</button>}
        </div>

        <div className="pj-summary">
          {columns.map(col=>{
            const count = bizTasks.filter(t=>t.status===col.id).length;
            return (
              <div key={col.id} className="pj-sum-item">
                <div className="pj-sum-dot" style={{background:col.color}}/>
                <span className="pj-sum-label">{col.label}</span>
                <span className="pj-sum-count">{count}</span>
              </div>
            );
          })}
        </div>

        {view==='kanban' && (
          <div className="pj-board">
            {columns.map(col=>(
              <Column key={col.id} col={col}
                tasks={bizTasks.filter(t=>t.status===col.id)}
                onDragStart={t=>{dragging.current=t}}
                onDrop={handleDrop}
                onCardClick={setSelected}
                onRename={renameStage}
                onDelete={deleteStage}/>
            ))}
            <div className="pj-col pj-col-add" onClick={addStage}
              style={{cursor:'pointer',background:'transparent',border:'1.5px dashed rgba(128,128,128,.3)',borderRadius:10,
                display:'flex',alignItems:'center',justifyContent:'center',minHeight:54,color:'#888',fontWeight:600,fontSize:13}}>
              + Add stage
            </div>
          </div>
        )}

        {view==='list' && (
          <div className="pj-list-wrap">
            <table className="pj-table">
              <thead><tr>{['Task','Service','Priority','Status','Assigned','Due'].map(h=><th key={h} className="pj-th">{h}</th>)}</tr></thead>
              <tbody>
                {bizTasks.map(t=>(
                  <tr key={t.id} className="pj-tr" onClick={()=>setSelected(t)}>
                    <td className="pj-td"><div className="pj-tr-title">{t.title}</div><div className="pj-tr-desc">{t.desc}</div></td>
                    <td className="pj-td pj-td-muted">{t.service||'—'}</td>
                    <td className="pj-td"><span className="pj-pri-pill" style={{background:PRIORITY_COLOR[t.priority]+'20',color:PRIORITY_COLOR[t.priority]}}>{t.priority}</span></td>
                    <td className="pj-td"><span className="pj-status-pill" style={{background:(columns.find(c=>c.id===t.status)?.color||'#6B7280')+'20',color:columns.find(c=>c.id===t.status)?.color||'#6B7280'}}>{columns.find(c=>c.id===t.status)?.label||t.status}</span></td>
                    <td className="pj-td pj-td-muted">{t.assigned}</td>
                    <td className={`pj-td${isOverdue(t.due,t.status)?' pj-td-over':' pj-td-muted'}`}>{fmtDate(t.due)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {selected && <TaskPanel task={selected} theme={theme} onClose={()=>setSelected(null)} onUpdate={handleUpdate} onDelete={handleDelete} teamNames={teamNames} columns={columns}/>}
      {showAdd   && <AddModal theme={theme} onClose={()=>setShowAdd(false)} onAdd={handleAdd} teamNames={teamNames} columns={columns}/>}
      {dialog && (
        <div className="pj-modal-overlay" onClick={()=>setDialog(null)}>
          <div className="pj-modal" data-theme={theme} style={{width:380,maxWidth:'92vw'}} onClick={e=>e.stopPropagation()}>
            <div className="pj-modal-hd">
              <span className="pj-modal-title">{dialog.title}</span>
              <button className="pj-icon-btn" onClick={()=>setDialog(null)}><CloseIcon/></button>
            </div>
            <div className="pj-modal-body">
              {dialog.mode === 'prompt' ? (
                <input autoFocus className="pj-inp" value={dialog.value}
                  onChange={e=>setDialog(d=>({...d, value:e.target.value}))}
                  onKeyDown={e=>{ if(e.key==='Enter'){ const fn=dialog.onConfirm, v=dialog.value; setDialog(null); fn(v); } }}/>
              ) : (
                <p style={{fontSize:13.5,lineHeight:1.6,opacity:.82,margin:0}}>{dialog.message}</p>
              )}
            </div>
            <div className="pj-modal-ft">
              {!dialog.okOnly && <button className="pj-btn-sec" onClick={()=>setDialog(null)}>Cancel</button>}
              <button className="pj-btn-pri" style={dialog.danger?{background:'#ef4444',borderColor:'#ef4444'}:{}}
                onClick={()=>{ const fn=dialog.onConfirm, v=dialog.value; setDialog(null); if(dialog.mode==='prompt') fn(v); else fn(); }}>
                {dialog.mode==='prompt' ? 'Save' : (dialog.confirmLabel || 'OK')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
